import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchOffhoursDigest } from '../services/publicPagesApi';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

interface OffhoursGroup {
    rule_id: number;
    rule_name: string;
    device_name: string | null;
    alarm_count: number;
    recovery_count: number;
    first_at: string | null;
    last_at: string | null;
    status: 'active' | 'recovered';
}

interface OffhoursData {
    contact_name: string;
    window_start: string | null;
    sent_at: string | null;
    alarm_count: number;
    groups: OffhoursGroup[];
}

const fmt = (iso: string | null) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('sv-SE', { timeZoneName: 'short' });
    } catch { return iso; }
};

const bodyStyle = publicBodyStyle;
const cardStyle = publicCardStyle(600);

const OffhoursPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [data, setData]         = useState<OffhoursData | null>(null);
    const [loading, setLoading]   = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!token) return;
        fetchOffhoursDigest(token)
            .then(setData)
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return <div style={bodyStyle}><div style={cardStyle}><p style={{ color: '#888' }}>Laddar…</p></div></div>;
    }
    if (notFound || !data) {
        return (
            <div style={bodyStyle}>
                <div style={cardStyle}>
                    <h1 style={{ color: '#c0392b' }}>Länken hittades inte</h1>
                    <p style={{ color: '#666' }}>Sammanställningen är ogiltig eller har tagits bort.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                <h1 style={{ margin: '0 0 4px', fontSize: 22, color: '#1e3a5f' }}>
                    🔔 Larm utanför arbetstid
                </h1>
                <p style={{ margin: '0 0 24px', color: '#555' }}>
                    {data.alarm_count} larm inträffade medan du var ledig
                    {data.contact_name ? <> — <strong>{data.contact_name}</strong></> : null}.
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14 }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: '4px 0', color: '#666', width: '45%' }}>Period från:</td>
                            <td style={{ padding: '4px 0', fontWeight: 500 }}>{fmt(data.window_start)}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '4px 0', color: '#666' }}>Levererad:</td>
                            <td style={{ padding: '4px 0', fontWeight: 500 }}>{fmt(data.sent_at)}</td>
                        </tr>
                    </tbody>
                </table>

                {data.groups.length === 0 ? (
                    <p style={{ color: '#666' }}>Inga larm att visa.</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {data.groups.map(g => (
                            <div key={g.rule_id} style={{
                                border: '1px solid #eee', borderRadius: 8, padding: '14px 16px',
                                borderLeft: `4px solid ${g.status === 'active' ? '#dc3545' : '#16a34a'}`,
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                    <strong style={{ fontSize: 15, color: '#1e3a5f' }}>{g.rule_name}</strong>
                                    <span style={{
                                        fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                                        background: g.status === 'active' ? '#fdecea' : '#f0fdf4',
                                        color: g.status === 'active' ? '#c0392b' : '#166534',
                                    }}>
                                        {g.status === 'active' ? 'Fortfarande aktiv' : 'Återställd'}
                                    </span>
                                </div>
                                {g.device_name && (
                                    <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{g.device_name}</div>
                                )}
                                <div style={{ fontSize: 13, color: '#444', marginTop: 8 }}>
                                    Larmade <strong>{g.alarm_count}</strong> gång{g.alarm_count === 1 ? '' : 'er'}
                                    {g.recovery_count > 0 && <> · {g.recovery_count} återställning{g.recovery_count === 1 ? '' : 'ar'}</>}
                                </div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                                    Först: {fmt(g.first_at)} · Senast: {fmt(g.last_at)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: '#aaa' }}>
                    DevIOT larmsammanställning
                </div>
            </div>
        </div>
    );
};

export default OffhoursPage;
